// ────────────────────────────────────────────────────────────────────────────
// CmdKContext
// ────────────────────────────────────────────────────────────────────────────
// Permite a cualquier página registrar "comandos contextuales" que aparecerán
// al tope del Global Search (Cmd+K) bajo el grupo "📍 En esta página".
//
// Uso básico en una página:
//
//   import { useRegisterCmdKCommands } from '../contexts/CmdKContext';
//
//   useRegisterCmdKCommands('oc-list', 'Órdenes de Compra', [
//     { id: 'filter-sin-vb', label: 'Filtrar: solo OCs sin VB',
//       icon: 'Filter', run: () => setSoloSinVb(true) },
//     { id: 'clear-selection', label: 'Limpiar selección', enabled: selected.size > 0,
//       run: () => setSelected(new Set()) },
//   ], [selected.size]);
//
// La lista se re-registra cuando cambian las `deps` (segundo arg del useEffect).
// ────────────────────────────────────────────────────────────────────────────
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const CmdKContext = createContext(null);

export function CmdKProvider({ children }) {
  // Map<namespace, { label, commands: Command[] }>
  const [registry, setRegistry] = useState({});

  const register = useCallback((namespace, label, commands) => {
    setRegistry((prev) => ({ ...prev, [namespace]: { label, commands: commands || [] } }));
  }, []);

  const unregister = useCallback((namespace) => {
    setRegistry((prev) => {
      if (!(namespace in prev)) return prev;
      const next = { ...prev };
      delete next[namespace];
      return next;
    });
  }, []);

  // Lista plana de comandos (solo los `enabled !== false`)
  const contextCommands = useMemo(() => {
    const out = [];
    for (const ns of Object.keys(registry)) {
      const { label, commands } = registry[ns];
      for (const c of commands) {
        if (c.enabled === false) continue;
        out.push({ ...c, _ns: ns, _nsLabel: label });
      }
    }
    return out;
  }, [registry]);

  const value = useMemo(() => ({
    register, unregister, contextCommands,
  }), [register, unregister, contextCommands]);

  return <CmdKContext.Provider value={value}>{children}</CmdKContext.Provider>;
}

export function useCmdK() {
  const ctx = useContext(CmdKContext);
  if (!ctx) {
    // Fallback seguro para que componentes usados fuera del provider no rompan.
    return { register: () => {}, unregister: () => {}, contextCommands: [] };
  }
  return ctx;
}

/**
 * Hook de conveniencia: registra comandos contextuales al montar la página
 * y los re-registra cuando cambian las `deps`. Se des-registran al desmontar.
 *
 * @param {string} namespace - id único por página (ej: 'oc-list')
 * @param {string} label - etiqueta legible (ej: 'Órdenes de Compra')
 * @param {Array}  commands - lista de comandos
 * @param {Array}  deps - deps del useEffect (igual que useEffect)
 */
export function useRegisterCmdKCommands(namespace, label, commands, deps = []) {
  const { register, unregister } = useCmdK();
  useEffect(() => {
    register(namespace, label, commands);
    return () => unregister(namespace);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
